import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { RefreshCw, Clock, ChevronDown, ChevronUp } from 'lucide-react';
import KitchenLayout from '../../components/KitchenLayout';
import { restaurantOrderService } from '../../services/restaurantOrderService';
import { RestaurantOrderListDTO, RestaurantOrderDTO } from '../../types';
import './KitchenDisplayPage.css';

// Kitchen only cares about orders that have been sent to it and not yet handed off.
// Ready orders stay visible until the front-of-house completes/settles them elsewhere.
const KITCHEN_STATUSES = ['Sent', 'InPreparation', 'Ready'] as const;
type KitchenStatus = typeof KITCHEN_STATUSES[number];

// Advance-only — the kitchen never sets Completed (that happens on settle) or Voided.
const NEXT_STATUS: Partial<Record<KitchenStatus, KitchenStatus>> = {
  Sent: 'InPreparation',
  InPreparation: 'Ready',
};

const POLL_INTERVAL_MS = 8000;

function elapsedMinutes(createdAt: string): number {
  // Backend serializes UTC timestamps without a trailing 'Z' — without it, Date
  // parses the string as local time and elapsed time is off by the UTC offset.
  const utcIso = createdAt.endsWith('Z') ? createdAt : createdAt + 'Z';
  return Math.max(0, Math.round((Date.now() - new Date(utcIso).getTime()) / 60000));
}

const KitchenDisplayPage: React.FC = () => {
  const { t } = useTranslation();
  const [orders, setOrders] = useState<RestaurantOrderListDTO[]>([]);
  const [details, setDetails] = useState<Record<number, RestaurantOrderDTO>>({});
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [advancingId, setAdvancingId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const load = useCallback(async (showSpinner: boolean) => {
    if (showSpinner) setLoading(true);
    try {
      const res = await restaurantOrderService.getAll(1, 100);
      const data = (res?.data as any)?.data?.data ?? (res?.data as any)?.data ?? [];
      const active = (Array.isArray(data) ? data : []).filter((o: RestaurantOrderListDTO) =>
        (KITCHEN_STATUSES as readonly string[]).includes(o.status)
      );
      setOrders(active);
      setError(null);
    } catch {
      setError(t('restaurant.kitchen.failedToLoad', 'Failed to load kitchen orders.'));
    } finally {
      if (showSpinner) setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load(true);
    pollRef.current = setInterval(() => load(false), POLL_INTERVAL_MS);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [load]);

  const toggleExpand = async (order: RestaurantOrderListDTO) => {
    if (expandedId === order.id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(order.id);
    if (!details[order.id]) {
      try {
        const res = await restaurantOrderService.getById(order.id);
        const d = (res.data as any)?.data ?? res.data;
        setDetails((prev) => ({ ...prev, [order.id]: d }));
      } catch { /* card still shows summary if detail fetch fails */ }
    }
  };

  const advance = async (order: RestaurantOrderListDTO) => {
    const next = NEXT_STATUS[order.status as KitchenStatus];
    if (!next) return;
    setAdvancingId(order.id);
    try {
      await restaurantOrderService.updateStatus(order.id, next);
      await load(false);
    } catch {
      setError(t('restaurant.kitchen.failedToUpdateStatus', 'Failed to update order status.'));
    } finally {
      setAdvancingId(null);
    }
  };

  const columns: { status: KitchenStatus; label: string }[] = [
    { status: 'Sent', label: t('restaurant.orderDetail.statusLabels.Sent', 'Sent') },
    { status: 'InPreparation', label: t('restaurant.orderDetail.statusLabels.InPreparation', 'In Preparation') },
    { status: 'Ready', label: t('restaurant.orderDetail.statusLabels.Ready', 'Ready') },
  ];

  return (
    <KitchenLayout>
      <div className="kds-page">
        <div className="kds-toolbar">
          <h1 className="kds-title">{t('restaurant.kitchen.title', 'Kitchen Display')}</h1>
          <button className="kds-refresh-btn" onClick={() => load(true)} disabled={loading}>
            <RefreshCw size={14} className={loading ? 'refresh-icon-spinning' : ''} />
            {t('common.refresh', 'Refresh')}
          </button>
        </div>

        {error && <div className="kds-error">{error}</div>}

        <div className="kds-board">
          {columns.map((col) => {
            const colOrders = orders
              .filter((o) => o.status === col.status)
              .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
            return (
              <div key={col.status} className={`kds-column kds-column--${col.status}`}>
                <div className="kds-column-header">
                  <span>{col.label}</span>
                  <span className="kds-column-count">{colOrders.length}</span>
                </div>
                <div className="kds-column-body">
                  {colOrders.length === 0 ? (
                    <div className="kds-empty">{t('restaurant.kitchen.empty', 'Nothing here.')}</div>
                  ) : (
                    colOrders.map((o) => {
                      const detail = details[o.id];
                      const expanded = expandedId === o.id;
                      const mins = elapsedMinutes(o.createdAt);
                      const next = NEXT_STATUS[o.status as KitchenStatus];
                      return (
                        <div key={o.id} className={`kds-ticket${mins >= 15 ? ' kds-ticket--late' : ''}`}>
                          <button className="kds-ticket-head" onClick={() => toggleExpand(o)}>
                            <div className="kds-ticket-head-main">
                              <span className="kds-ticket-number">{o.orderNumber}</span>
                              <span className="kds-ticket-sub">
                                {o.tableNumber ? `${t('restaurant.orderDetail.table', 'Table')} ${o.tableNumber}` : o.orderType}
                              </span>
                            </div>
                            <div className="kds-ticket-head-meta">
                              <span className="kds-ticket-elapsed"><Clock size={12} /> {mins}m</span>
                              {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                            </div>
                          </button>

                          {expanded && (
                            <div className="kds-ticket-lines">
                              {!detail ? (
                                <div className="kds-ticket-loading">{t('common.loading', 'Loading…')}</div>
                              ) : detail.lines.length === 0 ? (
                                <div className="kds-ticket-loading">{t('restaurant.kitchen.noItems', 'No items.')}</div>
                              ) : (
                                <ul>
                                  {detail.lines.map((l) => (
                                    <li key={l.id}>
                                      <span className="kds-line-qty">{l.quantity}×</span> {l.itemName}
                                      {(l.modifierNotes || l.notes) && (
                                        <span className="kds-line-note"> — {l.modifierNotes || l.notes}</span>
                                      )}
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          )}

                          {next && (
                            <button
                              className="kds-advance-btn"
                              disabled={advancingId === o.id}
                              onClick={() => advance(o)}
                            >
                              {advancingId === o.id
                                ? t('restaurant.orderDetail.updating', 'Updating…')
                                : col.status === 'Sent'
                                  ? t('restaurant.kitchen.startPreparing', 'Start Preparing')
                                  : t('restaurant.kitchen.markReady', 'Mark Ready')}
                            </button>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </KitchenLayout>
  );
};

export default KitchenDisplayPage;
